import {
  SelectOptionOld,
  useWSSnackbar,
  WSActions,
  WSDivider,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useRef, useState } from "react";
import * as Yup from "yup";
import {
  QUICKBOOKS_ENTITY,
  RefreshModal,
  useQboRefreshModal
} from "../../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryEngagement } from "../../../../query/engagements/queries/useQueryEngagement";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { useMutationCreatePayeeEngagement } from "../../../../query/payeeEngagements/mutations/useMutationCreatePayeeEngagement";
import { useQueryPayeeEngagementsListByPayeeId } from "../../../../query/payeeEngagements/queries/useQueryPayeeEngagementsListByPayeeId";
import { FormPartialPayeeSearchField } from "../../../../shared/components/PayeeSearchField";
import { selectorIsQBOActive } from "../../../../shared/selectors/selectorIsQBOActive";
import { selectQuickbooksAccountOrItemOptions } from "../../../Integrations/selectors/selectQuickbooksAccountOrItemOptions";
import { selectQuickbooksUserOptions } from "../../../Integrations/selectors/selectQuickbooksUserOptions";
import { getPayeeNames } from "../../selectors/getPayeeNames";
import { EngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { cleanOptionalFormValues } from "../../../../shared/utils/cleanOptionalFormValues";

export type CreateEngagementModalProps = {
  onClose(result: boolean): void;
  payeeId?: string;
  engagementId?: string;
};

type FormData = {
  payeeId: string;
  engagementId: string;
  engagementIds: string[];
  qboVendorId: string;
  qboExpenseAccountId: string;
};

export const CreateEngagementModal: React.FC<CreateEngagementModalProps> = ({
  payeeId,
  engagementId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const queryFeatureFlags = useFeatureFlags();
  const qboRefreshModal = useQboRefreshModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | undefined>();
  const submittingRef = useRef(false);

  const queryEngagementsTypes = useQueryEngagementsListAll(
    { payeeId, filter: { status: EngagementStatus.Active, isDefault: false } },
    {
      enabled: !!payeeId
    }
  );

  const queryPayeeEngagements = useQueryPayeeEngagementsListByPayeeId(
    payeeId!,
    {},
    {
      enabled: !!payeeId
    }
  );

  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();

  const queryPayee = useQueryPayee(payeeId!, {
    enabled: !!payeeId
  });

  const queryEngagement = useQueryEngagement(engagementId!, {
    enabled: !!engagementId
  });

  const isQuickbooksActive = selectorIsQBOActive(
    queryFeatureFlags?.data,
    queryIntegrationsQuickbooks.data
  );

  const [createEngagement, createEngagementMeta] =
    useMutationCreatePayeeEngagement();

  const quickbooksVendorOptions: SelectOptionOld[] =
    selectQuickbooksUserOptions(queryIntegrationsQuickbooksVendors.data);

  const quickbooksAccountsOptions: SelectOptionOld[] =
    selectQuickbooksAccountOrItemOptions(
      queryIntegrationsQuickbooksExpenseAccounts.data
    );

  const defaultAccount = (
    queryIntegrationsQuickbooksExpenseAccounts.data ?? []
  ).find(
    acct =>
      acct.accountId ===
      queryIntegrationsQuickbooks.data?.defaults?.expenseAccountId
  );

  const engagementsOptions =
    queryEngagementsTypes.data
      ?.filter(
        engagement =>
          !engagement.isDefault &&
          !queryPayeeEngagements.data?.some(
            eng => eng.engagementId === engagement.engagementId
          )
      )
      .map(engagement => ({
        label: engagement.name,
        value: engagement.engagementId
      })) || [];

  const payeeName = queryPayee.data
    ? getPayeeNames(queryPayee.data).getResolvedName()
    : "";

  const isQuickBooksFieldEnabled =
    isQuickbooksActive &&
    quickbooksVendorOptions.length > 0 &&
    quickbooksAccountsOptions.length > 0;

  // When engagementId is pre-set (from Engagement Details page), use single-select
  const isSingleEngagementMode = !!engagementId || isQuickBooksFieldEnabled;

  const handleSubmit = useCallback(
    async (data: FormData) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);
      setSubmitError(undefined);

      const idsToCreate = isSingleEngagementMode
        ? [data.engagementId]
        : data.engagementIds;

      const qboData =
        data.qboVendorId || data.qboExpenseAccountId
          ? cleanOptionalFormValues({
              quickbooks: {
                vendorId: data.qboVendorId,
                expenseAccountId: data.qboExpenseAccountId
              }
            })
          : undefined;

      let successCount = 0;
      let lastError: unknown;

      for (const id of idsToCreate) {
        try {
          await createEngagement(
            {
              payeeId: data.payeeId,
              payerOwnedData:
                isQuickBooksFieldEnabled && qboData
                  ? { integration: qboData }
                  : {},
              engagementId: id
            },
            {
              throwOnError: true
            }
          );
          successCount++;
        } catch (error) {
          lastError = error;
        }
      }

      submittingRef.current = false;
      setIsSubmitting(false);

      if (successCount > 0) {
        onClose(true);
        openSnackbar({
          message:
            successCount === 1
              ? "Assignment added successfully"
              : `${successCount} assignments added successfully`,
          type: "success"
        });

        if (lastError && successCount < idsToCreate.length) {
          const failedCount = idsToCreate.length - successCount;
          openSnackbar({
            message: `${failedCount} assignment${failedCount > 1 ? "s" : ""} failed to add`,
            type: "warning"
          });
        }
      } else if (lastError) {
        setSubmitError(
          lastError instanceof Error
            ? lastError
            : new Error("Failed to add assignments")
        );
      }
    },
    [
      createEngagement,
      isSingleEngagementMode,
      onClose,
      openSnackbar,
      isQuickBooksFieldEnabled
    ]
  );

  const validationSchema = isSingleEngagementMode
    ? Yup.object().shape({
        payeeId: Yup.string().required("Contractor is required!"),
        engagementId: Yup.string().required("Engagement is required").nullable()
      })
    : Yup.object().shape({
        payeeId: Yup.string().required("Contractor is required!"),
        engagementIds: Yup.array()
          .of(Yup.string())
          .min(1, "At least one engagement is required")
          .required("At least one engagement is required")
      });

  return (
    <WSElement
      shimmer={
        payeeId
          ? queryPayee.isLoading ||
            queryEngagementsTypes.isLoading ||
            queryPayeeEngagements.isLoading
          : queryEngagement.isLoading
      }
    >
      <WSForm<FormData>
        defaultValues={{
          qboVendorId: "",
          qboExpenseAccountId: "",
          engagementId: engagementId || "",
          engagementIds: [],
          payeeId: payeeId || ""
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <WSList gap="XL">
          {payeeId ? (
            <WSForm.Field
              name="payeeId"
              label="Contractor"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [{ value: payeeId || "", label: payeeName }]
              }}
            />
          ) : (
            <FormPartialPayeeSearchField
              name="payeeId"
              engagementId={engagementId!}
            />
          )}
          {isSubmitting ? (
            <WSElement py="L" shimmer fullWidth>
              <WSText>Loading</WSText>
            </WSElement>
          ) : engagementId ? (
            <WSForm.Field
              name="engagementId"
              label="Engagement"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [
                  {
                    value: engagementId,
                    label: queryEngagement.data?.name || "Current engagement"
                  }
                ]
              }}
            />
          ) : engagementsOptions.length > 0 ? (
            <WSForm.Field
              name={isQuickBooksFieldEnabled ? "engagementId" : "engagementIds"}
              label="Engagements"
              component={WSSelect}
              componentProps={{
                mode: isQuickBooksFieldEnabled ? "single" : "multiple",
                required: true,
                placeholder: "Select engagements",
                options: engagementsOptions
              }}
            />
          ) : (
            <WSText.ParagraphSm align="center">
              No engagements available
            </WSText.ParagraphSm>
          )}
          {isQuickBooksFieldEnabled && (
            <WSList gap="L">
              <WSDivider label="QuickBooks Mapping" />
              <WSInfoBox>
                Because you have an active integration with QBO, please map this
                new Contractor to an existing Vendor, or leave blank and we will
                create a new one. You can also set a default expense account for
                payable line items.
              </WSInfoBox>
              <WSForm.Field
                name="qboVendorId"
                component={WSSelect}
                componentProps={{
                  internalSearch: true,
                  placeholder: "Not mapped, create new Vendor",
                  options: quickbooksVendorOptions,
                  menuFooterAction: {
                    label: "Resync QBO Vendors",
                    icon: "refresh-v",
                    onClick: () => {
                      qboRefreshModal.open({
                        entity: QUICKBOOKS_ENTITY.VENDORS
                      });
                    }
                  }
                }}
                label="QBO Vendor"
              />
              <WSForm.Field
                name="qboExpenseAccountId"
                component={WSSelect}
                componentProps={{
                  internalSearch: true,
                  options: quickbooksAccountsOptions,
                  placeholder: `Use default "${defaultAccount?.fullyQualifiedName}"`,
                  menuFooterAction: {
                    label: "Resync QBO Expenses Accounts",
                    icon: "refresh-v",
                    onClick: () => {
                      qboRefreshModal.open({
                        entity: QUICKBOOKS_ENTITY.EXPENSES
                      });
                    }
                  }
                }}
                label="Default QBO Expense Account"
              />
              <RefreshModal />
            </WSList>
          )}

          <WSInfoBox title="About assignments">
            Assignments appear on the contractor's profile. They must complete
            any assignment-specific requirements to receive payments. You can
            archive or restore assignments at any time.
          </WSInfoBox>

          <WSErrorMessage
            contextKey="Engagement"
            error={submitError || createEngagementMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: isSingleEngagementMode
                  ? "Add assignment"
                  : "Add assignments",
                kind: "Primary",
                type: "submit",
                loading: isSubmitting
              },
              {
                label: "Cancel",
                kind: "Secondary",
                type: "button",
                onClick() {
                  onClose(false);
                }
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSElement>
  );
};
